import React, { useCallback, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { Button } from 'react-bootstrap';
import { useToast } from '../../hooks/toast';
import getResponseErrors from '../../utils/getResponseErrors';
import api from '../../services/api';
import { IPaciente } from '.';
import { IReturnInstanceInfo } from '../../pages/Whatsapp';
import { htmlDecode, replaceHTMLparaWhatsapp } from '../../utils/funcoes';
import Modal from '../Modal';
import Loading from '../Loading';

interface props {
  PacCodigo: number;
}

interface IPacienteEMensagem extends IPaciente {
  textoMensagem: string;
}

const DadosAcessoExames: React.FC<props> = ({ PacCodigo }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paciente, setPaciente] = useState<IPacienteEMensagem>();
  const [showModalDadosAcesso, setShowModalDadosAcesso] = useState(false);

  const handleGetPacienteEMensagem = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<IPacienteEMensagem>(
        `/pacientes/examacessdata/${PacCodigo}`,
      );

      setPaciente(response.data);
      setShowModalDadosAcesso(true);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível buscar dados',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [PacCodigo, addToast]);

  const handleStatusWhatsapp = useCallback(async () => {
    try {
      const response = await api.get<IReturnInstanceInfo>('whatsapp/status');
      const conectado = response.data.instance_data?.phone_connected || false;

      return conectado;
    } catch (error) {
      return false;
    }
  }, []);

  const handleEnviarWhatsapp = useCallback(async () => {
    try {
      if (!paciente || !paciente.PacCodigo) return;

      const conectado = await handleStatusWhatsapp();

      if (!conectado) {
        const textoDecode = htmlDecode(paciente.textoMensagem);
        const textoMensagem = encodeURIComponent(
          replaceHTMLparaWhatsapp(textoDecode),
        );

        const linkWhatsapp = `https://api.whatsapp.com/send?phone=55${paciente.PacCelular}&text=${textoMensagem}`;
        window.open(linkWhatsapp, '_blank');
      }

      if (conectado) {
        setLoading(true);
        const dadosMensagem = {
          message: paciente.textoMensagem,
          type: 'Envio acesso whatsapp',
          numeroTo: `55${paciente.PacCelular}`,
        };
        await api.put(
          `/whatsapp/sendmessage/${paciente.PacCodigo}`,
          dadosMensagem,
        );

        addToast({
          title: 'Envio de Acesso',
          type: 'success',
          description: 'Dados enviados com sucesso.',
        });
      }
    } catch (error) {
      const mensagemErroApi = getResponseErrors(error);

      addToast({
        title: 'Envio de Acesso',
        type: 'error',
        description: mensagemErroApi,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, handleStatusWhatsapp, paciente]);

  const handleEnviarEmail = useCallback(async () => {
    try {
      if (paciente && paciente.PacCodigo) {
        if (paciente.PacEmail === '') {
          addToast({
            title: 'Envio de Acesso',
            type: 'error',
            description: 'Email não cadastrado.',
          });
          return;
        }
        setLoading(true);
        const dadosMensagem = {
          PacNome:
            paciente.PacNomeSocial.trim() !== ''
              ? paciente.PacNomeSocial
              : paciente.PacNome,
          PacEmail: paciente.PacEmail,
          textoMensagem: paciente.textoMensagem,
        };
        await api.put(
          `/pacientes/enviaEmailDadosAcesso/${paciente.PacCodigo}`,
          dadosMensagem,
        );

        addToast({
          title: 'Envio de Acesso',
          type: 'success',
          description: 'Dados enviados com sucesso.',
        });
      }
    } catch (error) {
      const mensagemErroApi = getResponseErrors(error);

      addToast({
        title: 'Envio de Acesso',
        type: 'error',
        description: mensagemErroApi,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, paciente]);

  return (
    <>
      {loading && <Loading />}

      {showModalDadosAcesso && (
        <Modal
          handleFecharModal={() => setShowModalDadosAcesso(false)}
          show={showModalDadosAcesso}
          titulo="Enviar dados de Acesso"
          large
        >
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: htmlDecode(paciente?.textoMensagem || ''),
            }}
          />
          <div className="d-flex justify-content-between mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleEnviarWhatsapp}
              disabled={loading}
            >
              Enviar WhatsApp
            </Button>

            <CopyToClipboard
              text={replaceHTMLparaWhatsapp(paciente?.textoMensagem || '')}
            >
              <Button type="button" variant="info" size="sm" disabled={loading}>
                Copiar mensagem
              </Button>
            </CopyToClipboard>

            <Button
              type="button"
              variant="primary"
              onClick={handleEnviarEmail}
              size="sm"
              disabled={loading}
            >
              Enviar E-mail
            </Button>
          </div>
        </Modal>
      )}

      <Button
        type="button"
        variant="secondary"
        className="px-md-5"
        onClick={handleGetPacienteEMensagem}
      >
        Enviar acesso
      </Button>
    </>
  );
};

export default DadosAcessoExames;
